import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Reveal, InsightCard, inputCls } from "@/components/ui-bits";

export default function Insights() {
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/insights", { params: { search, category } }).then((r) => setArticles(r.data)).catch(() => {});
  }, [search, category]);

  const categories = useMemo(() => [...new Set(articles.map((a) => a.category).filter(Boolean))], [articles]);

  return (
    <div data-testid="insights-page">
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-24 lg:py-28">
        <img src={IMAGES.data} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{t("insights.eyebrow")}</p>
            <h1 className="max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl">{t("page.insightsTitle")}</h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">{t("page.insightsSub")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal className="mb-10 flex flex-wrap gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="relative min-w-64 flex-1">
              <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="insight-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("filter.search")} className={`${inputCls} ps-9`} />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setCategory("")} data-testid="insight-filter-all"
                className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${category === "" ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {t("filter.all")}
              </button>
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)} data-testid={`insight-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${category === c ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="insights-grid">
            {articles.map((a) => (
              <Reveal key={a.id}><InsightCard article={a} /></Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
